import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShoppingBag, ShoppingCart, Truck, MapPin, Calendar, Minus, Plus, ArrowLeft } from "lucide-react";
import { api, fileUrl, errMsg } from "@/lib/api";
import { addToCart } from "@/lib/cart";
import { WishlistHeart } from "@/components/WishlistHeart";
import { PipeText } from "@/lib/pipeText";

const MEMBERSHIPS = ["fan_club_membership", "venue_membership", "attraction_membership"];

export default function MarketplaceProduct({ slug }) {
  const nav = useNavigate();
  const [p, setP] = useState(undefined);
  const [variation, setVariation] = useState(null);
  const [qty, setQty] = useState(1);
  const [sched, setSched] = useState(null);
  const [img, setImg] = useState(0);
  const [earn, setEarn] = useState(null);

  useEffect(() => {
    api.get(`/marketplace/products/${slug}`).then((r) => {
      setP(r.data);
      if (r.data?.seller?.id) {
        api.get(`/loyalty/earn-preview?seller_id=${r.data.seller.id}&amount=${r.data.display_price}`)
          .then((x) => setEarn(x.data.points > 0 ? x.data : null)).catch(() => {});
      }
    }).catch(() => setP(null));
  }, [slug]);

  if (p === undefined) return <div className="py-40 text-center text-zinc-500">Loading...</div>;
  if (!p) return <div className="py-40 text-center text-zinc-400">Product not found. <Link to="/marketplace" className="text-rose-400">Back to marketplace</Link></div>;

  const images = (p.images?.length ? p.images : [p.image_url]).filter(Boolean);
  const price = (p.sale_price > 0 && p.sale_price < p.price ? p.sale_price : p.price) + (variation?.price_adjust || 0);
  const isActivity = p.product_type === "activity_booking";
  const isMembership = MEMBERSHIPS.includes(p.product_type);
  const avail = variation ? variation.available : p.available;
  const soldOut = p.status === "out_of_stock" || (avail !== null && avail !== undefined && avail <= 0);
  const needsVariation = (p.variations || []).filter((v) => v.status !== "inactive").length > 0 && !variation;

  const add = (buyNow) => {
    if (needsVariation) return toast.error("Please choose an option first");
    if (isActivity && !sched) return toast.error("Please pick a date & time");
    addToCart({
      product_id: p.id, variation_id: variation?.id || "", qty,
      name: p.name, variation_name: variation?.name || "", price, image: images[0] || "",
      slug: p.slug, product_type: p.product_type,
      meta: isActivity ? { schedule_id: sched.id, schedule_date: sched.date, schedule_time: sched.start_time } : {},
    });
    toast.success("Added to cart");
    if (buyNow) nav("/cart");
  };

  return (
    <div className="px-6 md:px-12 lg:px-20 py-10 min-h-screen" data-testid="mkt-product-page">
      <Link to="/marketplace" className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white mb-6"><ArrowLeft size={15} /> Marketplace</Link>
      <div className="grid lg:grid-cols-2 gap-10 items-start">
        <div>
          <div className="aspect-square bg-[#111] border border-white/10 rounded-2xl overflow-hidden relative">
            {images[img] ? <img src={fileUrl(images[img])} alt={p.name} className="w-full h-full object-cover" /> :
              <div className="w-full h-full flex items-center justify-center text-zinc-700"><ShoppingBag size={64} /></div>}
            <WishlistHeart productId={p.id} overlay size={20} className="!top-3 !right-3 !p-2.5" />
          </div>
          {images.length > 1 && (
            <div className="flex gap-2 mt-3">
              {images.map((im, i) => (
                <button key={`${im}-${i}`} onClick={() => setImg(i)} className={`w-16 h-16 rounded-lg overflow-hidden border ${img === i ? "border-rose-500" : "border-white/10"}`}>
                  <img src={fileUrl(im)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
        <div>
          <p className="overline text-rose-400">{p.seller?.display_name}</p>
          <h1 className="font-display font-extrabold text-3xl md:text-4xl">{p.name}</h1>
          {p.related_listing && (
            <Link to={`/${{ artist: "artists", venue: "venues", event: "events", attraction: "attractions", activity: "activities" }[p.related_listing.type]}/${p.related_listing.slug}`}
              className="inline-block mt-2 text-sm text-zinc-400 hover:text-rose-400" data-testid="mkt-related-link">
              For: <span className="text-white font-medium">{p.related_listing.name}</span> →
            </Link>
          )}
          <div className="flex items-baseline gap-3 mt-4">
            <span className="font-display font-extrabold text-3xl text-rose-400">${Number(price).toFixed(2)}</span>
            {p.sale_price > 0 && p.sale_price < p.price && <span className="text-zinc-500 line-through">${Number(p.price).toFixed(2)}</span>}
            {isMembership && <span className="text-zinc-400 text-sm">/ {p.billing_interval || "month"}</span>}
          </div>
          {p.short_description && <p className="text-zinc-300 mt-4">{p.short_description}</p>}
          {earn && (
            <p className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-full px-3 py-1.5" data-testid="mkt-earn-badge">
              ★ Earn ~{earn.points} {earn.points_name} with {earn.program_name}
            </p>
          )}

          {(p.variations || []).length > 0 && (
            <div className="mt-6">
              <p className="text-sm font-medium mb-2">Options</p>
              <div className="flex flex-wrap gap-2">
                {p.variations.filter((v) => v.status !== "inactive").map((v) => (
                  <button key={v.id} onClick={() => setVariation(variation?.id === v.id ? null : v)} data-testid={`mkt-variation-${v.id}`}
                    disabled={v.available !== null && v.available !== undefined && v.available <= 0}
                    className={`px-4 py-2 rounded-full text-sm border disabled:opacity-40 ${variation?.id === v.id ? "bg-rose-500 border-rose-500" : "border-white/15 hover:border-white/40"}`}>
                    {v.name}{v.price_adjust ? ` (+$${Number(v.price_adjust).toFixed(2)})` : ""}
                  </button>
                ))}
              </div>
            </div>
          )}

          {isActivity && (
            <div className="mt-6">
              <p className="text-sm font-medium mb-2 flex items-center gap-2"><Calendar size={15} /> Pick a date & time</p>
              {(p.schedules || []).length === 0 ? <p className="text-sm text-zinc-500">No upcoming availability — check back soon.</p> : (
                <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto">
                  {p.schedules.map((s) => {
                    const left = s.capacity ? s.capacity - (s.booked_capacity || 0) : null;
                    return (
                      <button key={s.id} onClick={() => setSched(sched?.id === s.id ? null : s)} disabled={left !== null && left <= 0} data-testid={`mkt-sched-${s.id}`}
                        className={`px-3 py-2 rounded-xl text-xs border disabled:opacity-40 ${sched?.id === s.id ? "bg-rose-500 border-rose-500" : "border-white/15 hover:border-white/40"}`}>
                        {s.date} {s.start_time}{left !== null && <span className="block text-[10px] opacity-70">{left} spots left</span>}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          <div className="flex items-center gap-4 mt-6">
            <div className="flex items-center border border-white/15 rounded-full">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="p-2.5" data-testid="mkt-qty-minus"><Minus size={14} /></button>
              <span className="w-8 text-center text-sm font-bold" data-testid="mkt-qty">{qty}</span>
              <button onClick={() => setQty(qty + 1)} className="p-2.5" data-testid="mkt-qty-plus"><Plus size={14} /></button>
            </div>
            {avail !== null && avail !== undefined && <span className="text-xs text-zinc-500">{avail} available</span>}
          </div>

          <div className="flex flex-col sm:flex-row gap-3 mt-6">
            <button onClick={() => add(false)} disabled={soldOut} data-testid="mkt-add-to-cart"
              className="flex-1 flex items-center justify-center gap-2 bg-white/10 hover:bg-white/15 disabled:opacity-40 font-bold px-6 py-3.5 rounded-full transition-colors">
              <ShoppingCart size={17} /> Add to cart
            </button>
            <button onClick={() => add(true)} disabled={soldOut} data-testid="mkt-buy-now"
              className="flex-1 bg-rose-500 hover:bg-rose-600 disabled:opacity-40 font-bold px-6 py-3.5 rounded-full transition-colors">
              {soldOut ? "Sold out" : "Buy now"}
            </button>
          </div>

          <div className="mt-8 space-y-2 text-sm text-zinc-400">
            {p.shipping_required && <p className="flex items-center gap-2"><Truck size={15} /> Ships{p.shipping_price > 0 ? ` — $${Number(p.shipping_price).toFixed(2)} shipping` : " free"}</p>}
            {p.pickup_available && <p className="flex items-center gap-2"><MapPin size={15} /> Local pickup available</p>}
            {p.refund_policy && <p className="text-xs text-zinc-500 mt-2">Refund policy: {p.refund_policy}</p>}
          </div>

          {p.description && (
            <div className="mt-8 pt-8 border-t border-white/10">
              <h2 className="font-display font-bold text-lg mb-2">About this {isMembership ? "membership" : "product"}</h2>
              <PipeText text={p.description} className="text-zinc-300 text-sm leading-relaxed" />
            </div>
          )}
          {isMembership && (p.benefits || []).length > 0 && (
            <div className="mt-6">
              <h3 className="font-display font-bold mb-2">Member benefits</h3>
              <ul className="space-y-1.5 text-sm text-zinc-300">
                {p.benefits.map((b, i) => <li key={`${b}-${i}`} className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-rose-500" />{b}</li>)}
              </ul>
            </div>
          )}
        </div>
      </div>

      {(p.more_from_seller || []).length > 0 && (
        <div className="mt-16">
          <h2 className="font-display font-bold text-xl mb-5">More from {p.seller?.display_name}</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {p.more_from_seller.map((m) => (
              <Link key={m.id} to={`/marketplace/${m.slug}`} className="bg-[#111] border border-white/10 rounded-2xl overflow-hidden hover:border-rose-500/50">
                <div className="aspect-square bg-[#0a0a0a]">{(m.images?.[0] || m.image_url) && <img src={fileUrl(m.images?.[0] || m.image_url)} alt={m.name} className="w-full h-full object-cover" />}</div>
                <div className="p-3"><p className="text-sm font-bold line-clamp-1">{m.name}</p><p className="text-rose-400 text-sm font-bold">${Number(m.display_price).toFixed(2)}</p></div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
